import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiLogOut, FiX, FiChevronDown } from "react-icons/fi";
import { SECTIONS, NAV_BOTTOM } from "../config/nav";

export default function Sidebar({
  open = false,
  onClose = () => {},
  onLogout,
  can = () => true,
}) {
  const location = useLocation();
  const [openSections, setOpenSections] = useState(() =>
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = !window.matchMedia("(min-width: 768px)").matches;
    document.body.style.overflow = open && isMobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggle = (key) =>
    setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden sb-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        id="app-sidebar"
        className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
        role="dialog"
        aria-modal="true"
      >
        <aside className="sb-root h-screen w-64 sticky top-0 flex flex-col overflow-hidden">
          {/* Ambient glow effects */}
          <div className="sb-glow sb-glow-1" />
          <div className="sb-glow sb-glow-2" />

          {/* Logo */}
          <div className="relative px-5 py-5 border-b border-white/[0.08]">
            <div className="flex items-center gap-3 sb-logo-enter">
              <div className="sb-logo-icon">
                <span className="text-white font-black text-sm relative z-10">Y</span>
              </div>
              <div className="min-w-0">
                <div className="text-[15px] font-extrabold tracking-wide text-white">
                  Yala Mobility
                </div>
                <div className="text-[10px] font-medium text-slate-400 tracking-widest uppercase">
                  Admin Dashboard
                </div>
              </div>
            </div>

            {/* Mobile close */}
            <button
              className="md:hidden absolute right-3 top-4 p-2 rounded-lg hover:bg-white/10 transition-all duration-200 active:scale-90"
              onClick={onClose}
              aria-label="Close menu"
            >
              <FiX className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto sb-scroll px-3 py-4 space-y-1">
            {SECTIONS.map((section, sIdx) => {
              const items = section.items.filter((i) => can(i.perm));
              if (!items.length) return null;
              const isOpen = openSections[section.key];

              return (
                <div key={section.key} className="sb-section" style={{ "--sb-delay": `${sIdx * 60}ms` }}>
                  {/* Section header */}
                  <button
                    onClick={() => toggle(section.key)}
                    className="sb-section-header group w-full flex items-center justify-between px-2 py-2 mb-1"
                  >
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 group-hover:text-slate-300 transition-colors">
                      {section.label}
                    </span>
                    <FiChevronDown
                      className={`w-3 h-3 text-slate-600 group-hover:text-slate-400 transition-all duration-300 ${
                        isOpen ? "rotate-0" : "-rotate-90"
                      }`}
                    />
                  </button>

                  {/* Items */}
                  <div className={`sb-items-wrap ${isOpen ? "sb-items-open" : "sb-items-closed"}`}>
                    <nav className="space-y-0.5">
                      {items.map((item, iIdx) => {
                        const isActive = location.pathname === item.to;
                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            className={`sb-nav-item ${isActive ? "sb-nav-active" : "sb-nav-idle"}`}
                            onClick={onClose}
                            style={{ "--item-delay": `${iIdx * 30}ms` }}
                          >
                            {/* Active indicator bar */}
                            <div className={`sb-active-bar ${isActive ? "sb-active-bar-on" : ""}`} />

                            <item.icon className={`w-[18px] h-[18px] shrink-0 transition-all duration-200 ${
                              isActive ? "text-emerald-400" : "text-slate-500 group-hover:text-slate-300"
                            }`} />

                            <span className="truncate text-sm">{item.label}</span>

                            {/* Active dot */}
                            {isActive && <div className="sb-active-dot" />}
                          </NavLink>
                        );
                      })}
                    </nav>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom */}
          <div className="p-3 border-t border-white/[0.06] space-y-0.5 sb-bottom-enter">
            {NAV_BOTTOM.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sb-nav-item ${isActive ? "sb-nav-active" : "sb-nav-idle"}`
                }
                onClick={onClose}
              >
                <item.icon className="w-[18px] h-[18px] shrink-0 text-slate-500" />
                <span className="truncate text-sm">{item.label}</span>
              </NavLink>
            ))}

            <button
              onClick={onLogout}
              className="sb-nav-item sb-logout w-full text-left"
              aria-label="Logout"
            >
              <FiLogOut className="w-[18px] h-[18px] shrink-0" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </aside>
      </div>
    </>
  );
}
